import {
  Children,
  Fragment,
  isValidElement,
  memo,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { Button } from '@dynatrace/strato-components/buttons';
import { DotMenuIcon } from '@dynatrace/strato-icons';

import { rowActionsCSS } from './RowActions.sty.js';
import { Menu, type MenuProps } from '../../../../navigation/menu/Menu.js';
import { isTrigger as isMenuTrigger } from '../../../../navigation/menu/Trigger.js';
import { DataTableV2DefaultCell } from '../../components/DataTableV2DefaultCell.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import { type ColumnDef } from '../../hooks/useTable/types.js';

/**
 * Internal wrapper component for the row actions children.
 * @internal
 */
const DataTableV2InternalRowActions = memo((props: PropsWithChildren) => {
  const table = useDataTableV2();
  const intl = useIntl();
  const density =
    table.getVisualVariantOptions().rowDensity === 'condensed'
      ? 'condensed'
      : 'default';
  const ariaLabel = intl.formatMessage({
    id: 'L/D6X7Ee5PYWzl3i',
    defaultMessage: 'Show more actions',
  });

  const [open, setOpen] = useState(false);

  const { menu, freeformSlot } = useMemo(() => {
    const slots: {
      menu: ReactElement<MenuProps> | null;
      freeformSlot: ReactNode[];
    } = {
      menu: null,
      freeformSlot: [],
    };

    const childrenArray = Children.toArray(props.children);
    for (const child of childrenArray) {
      if (isValidElement<MenuProps>(child) && child.type === Menu) {
        slots.menu = child;
        continue;
      }
      slots.freeformSlot.push(child);
    }
    return slots;
  }, [props.children]);

  if (!menu) {
    return freeformSlot;
  }

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (open === false) {
    return (
      <>
        {freeformSlot}
        <span onClick={() => setOpen(true)} style={{ display: 'contents' }}>
          {getMenuTrigger(menu, density, ariaLabel)}
        </span>
      </>
    );
  }

  return (
    <>
      {freeformSlot}
      <Menu {...menu.props} open={open} onOpenChange={setOpen}>
        <Menu.Trigger>
          <Button variant="default" size={density} aria-label={ariaLabel}>
            <Button.Prefix>
              <DotMenuIcon />
            </Button.Prefix>
          </Button>
        </Menu.Trigger>
        {menu.props.children}
      </Menu>
    </>
  );
});

/**
 * @returns the custom menu trigger if present, or a default menu trigger (dot menu icon button) otherwise.
 */
function getMenuTrigger(
  menu: ReactElement<MenuProps>,
  size?: 'default' | 'condensed',
  ariaLabel?: string,
) {
  const menuChildren = Children.toArray(menu.props.children) as ReactElement[];
  const customMenuTrigger = menuChildren.find(isMenuTrigger);
  if (customMenuTrigger) {
    return customMenuTrigger.props.children;
  }
  return getDefaultMenuTrigger(size, ariaLabel);
}

function getDefaultMenuTrigger(
  size?: 'default' | 'condensed',
  ariaLabel?: string,
) {
  return (
    <Button variant="default" size={size} aria-label={ariaLabel}>
      <Button.Prefix>
        <DotMenuIcon />
      </Button.Prefix>
    </Button>
  );
}

function isFragment(
  child: ReactElement,
): child is ReactElement<PropsWithChildren> {
  return child.type === Fragment;
}

/**
 * Column definition for the Action Column
 * @internal
 */
export const ROW_ACTIONS_COLUMN: ColumnDef<unknown, unknown> = {
  id: 'DataTableV2RowActions',
  accessorFn: () => '',
  header: '',
  width: 'content',
  enableSorting: false,
  enableUserActions: false,
  enableHiding: false,
  enableResizing: false,
  meta: {
    builtin: true,
    builtinPlacement: 'suffix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
  },
  cell: ({ row, table }) => {
    if (table.options.rowActions === undefined) {
      return null;
    }
    const actions = table.options.rowActions(row.original, {
      rowDensity: table.getVisualVariantOptions().rowDensity,
      rowIndex: row.index,
      rowId: row.id,
    });

    if (isElement(actions)) {
      return (
        <DataTableV2DefaultCell className={rowActionsCSS}>
          <DataTableV2InternalRowActions>
            {isFragment(actions) ? actions.props.children : actions}
          </DataTableV2InternalRowActions>
        </DataTableV2DefaultCell>
      );
    }
  },
};
